import { format } from 'date-fns'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useAuth, useToast } from '@/hooks'
import type { Notificacao } from '@/models'
import {
  FORMATO_DA_DATA_DA_NOTIFICACAO,
  useMarcarNotificacaoComoVisualizada,
  useMarcarTodasAsNotificacoesComoVisualizadas,
  useObterNotificacoes,
} from '@/services/notificacoes'

import type { NotificacoesContextoData } from './tipos'

const INTERVALO_DE_POLLING_EM_MS = 60_000

export function useBloc(): NotificacoesContextoData {
  const { estaAutenticado } = useAuth()
  const toast = useToast()

  const [obterNotificacoes] = useObterNotificacoes()
  const [marcarNotificacaoComoVisualizada] =
    useMarcarNotificacaoComoVisualizada()
  const [marcarTodasAsNotificacoesComoVisualizadas] =
    useMarcarTodasAsNotificacoesComoVisualizadas()

  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([])
  const [carregando, setCarregando] = useState<boolean>(false)
  const [carregandoMais, setCarregandoMais] = useState<boolean>(false)
  const [erro, setErro] = useState<boolean>(false)
  const [pagina, setPagina] = useState<number>(1)
  const [temMais, setTemMais] = useState<boolean>(false)
  const [popupAberto, setPopupAberto] = useState<boolean>(false)

  const popupAbertoRef = useRef<boolean>(false)
  const notificacoesRef = useRef<Notificacao[]>([])

  useEffect(() => {
    popupAbertoRef.current = popupAberto
  }, [popupAberto])

  useEffect(() => {
    notificacoesRef.current = notificacoes
  }, [notificacoes])

  const recarregarNotificacoes = useCallback(async (): Promise<void> => {
    setCarregando(true)
    setErro(false)

    const resposta = await obterNotificacoes(1)

    if (!resposta) {
      setErro(true)
      setCarregando(false)
      return
    }

    setNotificacoes(resposta.resultado)
    setPagina(1)
    setTemMais(resposta.temMais)
    setCarregando(false)
  }, [obterNotificacoes])

  const carregarMaisNotificacoes = useCallback(async (): Promise<void> => {
    if (!temMais) return

    const proximaPagina = pagina + 1

    setCarregandoMais(true)

    const resposta = await obterNotificacoes(proximaPagina)

    if (!resposta) {
      setCarregandoMais(false)

      toast.erro('Aconteceu um erro', {
        descricao: 'Não foi possível carregar mais notificações no momento.',
      })

      return
    }

    setNotificacoes(anteriores => [...anteriores, ...resposta.resultado])
    setPagina(proximaPagina)
    setTemMais(resposta.temMais)
    setCarregandoMais(false)
  }, [obterNotificacoes, pagina, temMais])

  const marcarComoLida = useCallback(
    async (idDaNotificacao: number): Promise<void> => {
      const visualizadoEm = format(new Date(), FORMATO_DA_DATA_DA_NOTIFICACAO)

      // Atualização otimista: o indicador some antes da resposta do back-end.
      setNotificacoes(anteriores =>
        anteriores.map(notificacao =>
          notificacao.id === idDaNotificacao && !notificacao.visualizado_em
            ? { ...notificacao, visualizado_em: visualizadoEm }
            : notificacao
        )
      )

      const sucesso = await marcarNotificacaoComoVisualizada(idDaNotificacao)

      if (sucesso) return

      setNotificacoes(anteriores =>
        anteriores.map(notificacao =>
          notificacao.id === idDaNotificacao
            ? { ...notificacao, visualizado_em: null }
            : notificacao
        )
      )
    },
    [marcarNotificacaoComoVisualizada]
  )

  const marcarTodasComoLidas = useCallback(async (): Promise<void> => {
    const visualizadoEm = format(new Date(), FORMATO_DA_DATA_DA_NOTIFICACAO)

    const notificacoesAntesDaAlteracao = notificacoesRef.current

    setNotificacoes(anteriores =>
      anteriores.map(notificacao =>
        notificacao.visualizado_em
          ? notificacao
          : { ...notificacao, visualizado_em: visualizadoEm }
      )
    )

    const sucesso = await marcarTodasAsNotificacoesComoVisualizadas()

    if (sucesso) return

    setNotificacoes(notificacoesAntesDaAlteracao)
  }, [marcarTodasAsNotificacoesComoVisualizadas])

  const adicionarNotificacao = useCallback((notificacao: Notificacao): void => {
    setNotificacoes(anteriores => [notificacao, ...anteriores])
  }, [])

  const verificarNovasNotificacoes = useCallback(async (): Promise<void> => {
    const resposta = await obterNotificacoes(1)

    if (!resposta) return

    setNotificacoes(anteriores => {
      const idsExistentes = new Set(anteriores.map(({ id }) => id))
      const novas = resposta.resultado.filter(
        notificacao => !idsExistentes.has(notificacao.id)
      )

      return novas.length > 0 ? [...novas, ...anteriores] : anteriores
    })
  }, [obterNotificacoes])

  useEffect(() => {
    if (!estaAutenticado) {
      setNotificacoes([])
      setPagina(1)
      setTemMais(false)
      setErro(false)
      return
    }

    recarregarNotificacoes()

    const intervalo = setInterval(() => {
      if (popupAbertoRef.current) {
        verificarNovasNotificacoes()
        return
      }

      recarregarNotificacoes()
    }, INTERVALO_DE_POLLING_EM_MS)

    return () => {
      clearInterval(intervalo)
    }
  }, [estaAutenticado, recarregarNotificacoes, verificarNovasNotificacoes])

  const quantidadeNaoVisualizadas = useMemo(
    () =>
      notificacoes.filter(notificacao => !notificacao.visualizado_em).length,
    [notificacoes]
  )

  return useMemo(
    () => ({
      notificacoes,
      carregando,
      carregandoMais,
      erro,
      quantidadeNaoVisualizadas,
      existemNaoVisualizadas: quantidadeNaoVisualizadas > 0,
      podeCarregarMais: temMais,
      popupAberto,

      setPopupAberto,
      recarregarNotificacoes,
      carregarMaisNotificacoes,
      marcarComoLida,
      marcarTodasComoLidas,
      adicionarNotificacao,
    }),
    [
      notificacoes,
      carregando,
      carregandoMais,
      erro,
      quantidadeNaoVisualizadas,
      temMais,
      popupAberto,

      setPopupAberto,
      recarregarNotificacoes,
      carregarMaisNotificacoes,
      marcarComoLida,
      marcarTodasComoLidas,
      adicionarNotificacao,
    ]
  )
}
